'use client';

import React from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import CheckoutInner from '@/components/checkout/checkoutInner';

import { calculateOrderTotals } from '@/lib/functions/calculateOrderTotals';
import { convertPriceFromDollarsToCents } from '@/lib/functions/convertDollarsToCents';
import { useCheckoutShippingMethods } from '@/lib/hooks/useCheckoutShippingMethods';

import type { OrderPayload } from '@/lib/types/orderPayload';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY || '');

interface CheckoutStepProps {
  orderData: OrderPayload;
  refreshCart: (showLoader: boolean) => void;
  setOrderData: React.Dispatch<React.SetStateAction<OrderPayload | null>>;
  onOpenAddressModal: () => void;
  onEditCart: () => void;
}

export default function CheckoutStep({
  orderData,
  refreshCart,
  setOrderData,
  onOpenAddressModal,
  onEditCart,
}: CheckoutStepProps) {
  const { authTotal } = calculateOrderTotals(orderData.pricing, true);
  const orderTotalInCents = convertPriceFromDollarsToCents(authTotal) ?? 0;
  const checkoutShippingMethods = useCheckoutShippingMethods(orderData);

  if (orderTotalInCents <= 0) {
    return null;
  }

  const elementsOptions: StripeElementsOptions = {
    mode: 'payment',
    capture_method: 'manual',
    paymentMethodTypes: ['card'],
    amount: orderTotalInCents,
    currency: 'usd',
    appearance: {
      theme: 'stripe',
    },
  };

  // Stable key so Stripe amount updates via options; remounting on total wipes checkout form state (specs-react pattern).
  return (
    <Elements key={orderData.token || 'checkout'} stripe={stripePromise} options={elementsOptions}>
      <CheckoutInner
        orderData={orderData}
        refreshCart={refreshCart}
        setOrderData={setOrderData}
        onOpenAddressModal={onOpenAddressModal}
        onEditCart={onEditCart}
        displayedShippingMethods={checkoutShippingMethods.displayedShippingMethods}
        shippingMethodsLoading={checkoutShippingMethods.shippingMethodsLoading}
        hasLoadedShippingMethods={checkoutShippingMethods.hasLoadedShippingMethods}
      />
    </Elements>
  );
}
